import React from "react";
import { Link } from "react-router-dom";
import { Search, CalendarCheck, CheckCircle2, CreditCard, PlusCircle, Tag, Inbox, Wallet, ArrowRight, Percent, Receipt, BadgeCheck, Sparkles } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Reveal } from "@/components/Reveal";
import { FoundingBadge } from "@/components/FoundingBadge";
import { Button } from "@/components/ui/button";

export default function HowItWorks() {
  const { t } = useLang();

  const bookSteps = [
    { icon: Search, t: t("hiw_book_1t"), d: t("hiw_book_1d") },
    { icon: CalendarCheck, t: t("hiw_book_2t"), d: t("hiw_book_2d") },
    { icon: CheckCircle2, t: t("hiw_book_3t"), d: t("hiw_book_3d") },
    { icon: CreditCard, t: t("hiw_book_4t"), d: t("hiw_book_4d") },
  ];
  const offerSteps = [
    { icon: PlusCircle, t: t("hiw_offer_1t"), d: t("hiw_offer_1d") },
    { icon: Tag, t: t("hiw_offer_2t"), d: t("hiw_offer_2d") },
    { icon: Inbox, t: t("hiw_offer_3t"), d: t("hiw_offer_3d") },
    { icon: Wallet, t: t("hiw_offer_4t"), d: t("hiw_offer_4d") },
  ];

  const Column = ({ title, steps, cta, to, accent }) => (
    <div className="glass-card rounded-3xl p-7 lg:p-9">
      <h2 className="font-display text-2xl font-medium tracking-tight text-ppc-text lg:text-3xl">
        {title}
      </h2>
      <div className="mt-8 space-y-6">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={i} delay={i * 0.08}>
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span
                    className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-ppc bg-ppc-bg"
                  >
                    <Icon className="h-5 w-5" style={{ color: accent }} />
                  </span>
                  {i < steps.length - 1 && <span className="mt-2 h-full w-px flex-1 bg-ppc-border" />}
                </div>
                <div className="pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-[0.2em] text-ppc-muted">
                      0{i + 1}
                    </span>
                    <h3 className="font-display text-lg font-medium text-ppc-text">{s.t}</h3>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-ppc-muted">{s.d}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Button
        asChild
        data-testid={`hiw-cta-${to.replace("/", "") || "home"}`}
        className="mt-6 gap-1.5 rounded-xl btn-primary"
      >
        <Link to={to}>
          {cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-5 pb-28 pt-16 md:pb-16 lg:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="font-display text-4xl font-medium tracking-tighter text-ppc-text lg:text-6xl">
            {t("hiw_title")}
          </h1>
          <p className="mt-4 text-lg text-ppc-muted">{t("hiw_sub")}</p>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <Column
            title={t("hiw_book_title")}
            steps={bookSteps}
            cta={t("hiw_cta_book")}
            to="/browse"
            accent="#9184d9"
          />
        </Reveal>
        <Reveal delay={0.1}>
          <Column
            title={t("hiw_offer_title")}
            steps={offerSteps}
            cta={t("hiw_cta_offer")}
            to="/create-listing"
            accent="#c6bff0"
          />
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <section className="mt-16" data-testid="fees-section">
          <h2 className="font-display text-2xl tracking-tight text-ppc-text lg:text-3xl">{t("fees_title")}</h2>
          <p className="mt-2 text-ppc-muted">{t("fees_sub")}</p>

          <div className="mt-8 grid gap-4 lg:grid-cols-[1.2fr_1fr]">
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { icon: BadgeCheck, t: t("fee_service"), d: t("fee_service_d") },
                { icon: Percent, t: t("fee_booking"), d: t("fee_booking_d") },
                { icon: Receipt, t: t("fee_stripe"), d: t("fee_stripe_d") },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} className="card-list p-5" data-testid={`fee-card-${i}`}>
                    <span className="grid h-10 w-10 place-items-center rounded-xl border border-ppc bg-ppc-bg">
                      <Icon className="h-[18px] w-[18px]" style={{ color: "#9184d9" }} />
                    </span>
                    <div className="mt-4 flex items-center gap-2">
                      <span className="label-caps">0{i + 1}</span>
                      <h3 className="text-[15px] text-ppc-text">{c.t}</h3>
                    </div>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-ppc-muted">{c.d}</p>
                  </div>
                );
              })}
            </div>

            <div className="card-focal p-6" data-testid="fee-example">
              <p className="label-caps">{t("fee_example")}</p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-ppc-muted"><span>{t("fee_service")}</span><span className="text-ppc-text">€100.00</span></div>
                <div className="flex justify-between text-ppc-muted"><span>{t("fee_booking")}</span><span className="text-ppc-text">€10.00</span></div>
                <div className="flex justify-between text-ppc-muted"><span>{t("fee_stripe")} · 1.5% + €0.25</span><span className="text-ppc-text">€1.93</span></div>
                <div className="flex items-center justify-between border-t border-ppc pt-3">
                  <span className="font-medium text-ppc-text">{t("fee_total")}</span>
                  <span className="font-display text-2xl text-primary-green">€111.93</span>
                </div>
                <div className="flex items-center justify-between rounded-xl st-paid px-3 py-2">
                  <span>{t("fee_provider_gets")}</span>
                  <span className="font-medium">€100.00</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card-list mt-4 flex flex-col gap-4 p-6 sm:flex-row sm:items-start" data-testid="fee-early-access">
            <span className="avatar-grad grid h-11 w-11 shrink-0 place-items-center rounded-xl">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-[15px] text-ppc-text">{t("fee_early_title")}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ppc-muted">{t("fee_early_d")}</p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[13px]">
                <span className="text-ppc-muted">{t("fee_provider_gets")}: <b className="font-medium text-primary-green">100%</b></span>
                <span className="text-ppc-muted">{t("fee_booking")}: <b className="font-medium text-ppc-text">{t("fee_paid_by_client")}</b></span>
              </div>
            </div>
          </div>
          <div className="card-focal mt-4 flex flex-col gap-4 p-6 sm:flex-row sm:items-start" data-testid="founding-section">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-ppc bg-ppc-bg">
              <Sparkles className="h-5 w-5" style={{ color: "#e8c478" }} />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-[15px] text-ppc-text">{t("founding_title")}</h3>
                <FoundingBadge fm={{ number: 1, city: "Milan" }} />
                <span className="chip chip-active text-[11px]">{t("founding_scope")}</span>
              </div>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ppc-muted">{t("founding_d")}</p>
              <p className="mt-3 text-[13px] text-primary-green">{t("founding_promo")}</p>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
